import { useEffect, useRef } from 'react'

export default function usePageVisible(
  call: (string: boolean) => void,
  delay?: number,
): void {
  const timeoutId = useRef(null)

  const browserCompatApi = () => {
    let hidden, visibilityChange
    if ('hidden' in document) {
      hidden = 'hidden'
      visibilityChange = 'visibilitychange'
    } else if ('mozHidden' in document) {
      // Firefox up to v17
      hidden = 'mozHidden'
      visibilityChange = 'mozvisibilitychange'
    } else if ('webkitHidden' in document) {
      // Chrome up to v32, Android up to v4.4, Blackberry up to v10
      hidden = 'webkitHidden'
      visibilityChange = 'webkitvisibilitychange'
    }
    return {
      hidden,
      visibilityChange,
    }
  }

  const cleanupTimeout = () => clearTimeout(timeoutId.current)

  useEffect(() => {
    const { hidden, visibilityChange } = browserCompatApi()

    const handleVisibilityChange = () => {
      if (delay) {
        if (delay < 0) {
          throw new Error('delay must be a positive integer')
        }

        if (timeoutId.current) cleanupTimeout()
        timeoutId.current = setTimeout(() => call(!document[hidden]), delay)
      } else {
        call(!document[hidden])
      }
    }

    document.addEventListener(visibilityChange, handleVisibilityChange)

    return () => {
      document.removeEventListener(visibilityChange, handleVisibilityChange)
    }
  }, [call, delay])
}
